import { CampaignEntryActionType, CampaignTargetSnsType, CampaignType } from '@/typings/campaigns'

export interface EntryActionLabel {
  entryActionType: CampaignEntryActionType
  label: string
}

interface CampaignEntryActionDefinition extends EntryActionLabel {
  targetSns: CampaignTargetSnsType
  campaignType: CampaignType
}

/**
 * targetSns × campaignType × entryActionType 許可リスト.
 */
const CAMPAIGN_ENTRY_ACTION_DEFINITIONS: readonly CampaignEntryActionDefinition[] = [
  // twitter × keyword
  {
    targetSns: 'twitter',
    campaignType: 'keyword',
    entryActionType: 'retweet',
    label: 'フォロー＆リポスト(公式リポスト)'
  },
  { targetSns: 'twitter', campaignType: 'keyword', entryActionType: 'quote_tweet', label: 'フォロー＆リポスト(引用)' },
  {
    targetSns: 'twitter',
    campaignType: 'keyword',
    entryActionType: 'retweet_and_quote_tweet',
    label: 'フォロー＆リポスト(全て)'
  },
  { targetSns: 'twitter', campaignType: 'keyword', entryActionType: 'tweet', label: 'フォロー＆ポスト(通常)' },
  {
    targetSns: 'twitter',
    campaignType: 'keyword',
    entryActionType: 'tweet_media',
    label: 'フォロー＆ポスト(画像または動画)'
  },
  { targetSns: 'twitter', campaignType: 'keyword', entryActionType: 'mention', label: 'フォロー＆メンション' },

  // twitter × old_web_instantwin
  {
    targetSns: 'twitter',
    campaignType: 'old_web_instantwin',
    entryActionType: 'retweet',
    label: 'リポスト(公式リポスト)'
  },
  { targetSns: 'twitter', campaignType: 'old_web_instantwin', entryActionType: 'quote_tweet', label: 'リポスト(引用)' },
  {
    targetSns: 'twitter',
    campaignType: 'old_web_instantwin',
    entryActionType: 'retweet_and_quote_tweet',
    label: 'リポスト(全て)'
  },
  { targetSns: 'twitter', campaignType: 'old_web_instantwin', entryActionType: 'mention', label: 'メンション' },

  // twitter × instantwin
  { targetSns: 'twitter', campaignType: 'instantwin', entryActionType: 'retweet', label: 'リポスト(公式リポスト)' },
  { targetSns: 'twitter', campaignType: 'instantwin', entryActionType: 'quote_tweet', label: 'リポスト(引用)' },
  {
    targetSns: 'twitter',
    campaignType: 'instantwin',
    entryActionType: 'retweet_and_quote_tweet',
    label: 'リポスト(全て)'
  },
  { targetSns: 'twitter', campaignType: 'instantwin', entryActionType: 'mention', label: 'メンション' },

  // instagram
  { targetSns: 'instagram', campaignType: 'keyword', entryActionType: 'comment', label: 'コメント' },
  { targetSns: 'instagram', campaignType: 'keyword', entryActionType: 'mention', label: '画像投稿' },
  { targetSns: 'instagram', campaignType: 'instantwin', entryActionType: 'comment', label: 'コメント' },

  // tiktok
  { targetSns: 'tiktok', campaignType: 'keyword', entryActionType: 'comment', label: 'コメント' }
]

/**
 * targetSns × campaignType に対して、entryActionType の妥当性を判定.
 */
export function isCampaignEntryActionAllowed(
  targetSns: CampaignTargetSnsType,
  campaignType: CampaignType,
  entryActionType: string
): boolean {
  return CAMPAIGN_ENTRY_ACTION_DEFINITIONS.some(
    def => def.targetSns === targetSns && def.campaignType === campaignType && def.entryActionType === entryActionType
  )
}

/**
 * targetSns × campaignType に応じて、entryActionType の一覧をフィルタリング.
 */
export function filterAllowedCampaignEntryActionTypes(
  targetSns: CampaignTargetSnsType | undefined,
  campaignType: CampaignType | undefined,
  entryActionTypes: readonly string[]
): string[] {
  if (!targetSns || !campaignType) {
    return []
  }
  return entryActionTypes.filter(entryActionType =>
    isCampaignEntryActionAllowed(targetSns, campaignType, entryActionType)
  )
}

/**
 * targetSns × campaignType × entryActionType に対応する表示ラベルを取得.
 */
export function findEntryActionLabel(
  targetSns: CampaignTargetSnsType,
  campaignType: CampaignType,
  entryActionType: CampaignEntryActionType
): string | undefined {
  return CAMPAIGN_ENTRY_ACTION_DEFINITIONS.find(
    def => def.targetSns === targetSns && def.campaignType === campaignType && def.entryActionType === entryActionType
  )?.label
}

/**
 * targetSns × campaignType に対応する entryActionType とラベルの一覧を取得.
 */
export function listEntryActionLabels(
  targetSns: CampaignTargetSnsType,
  campaignType: CampaignType
): readonly EntryActionLabel[] {
  return CAMPAIGN_ENTRY_ACTION_DEFINITIONS.filter(
    def => def.targetSns === targetSns && def.campaignType === campaignType
  ).map(def => ({ entryActionType: def.entryActionType, label: def.label }))
}
